export type DetectedEncoding = 'utf-8' | 'utf-8-bom' | 'utf-16le' | 'utf-16be' | 'windows-1252' | 'unknown'

const SAMPLE_BYTES = 64 * 1024

const detectFromBom = (bytes: Uint8Array): DetectedEncoding | null => {
    if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return 'utf-8-bom'
    if (bytes[0] === 0xff && bytes[1] === 0xfe) return 'utf-16le'
    if (bytes[0] === 0xfe && bytes[1] === 0xff) return 'utf-16be'
    return null
}

// UTF-16 without a BOM shows up as lots of zero bytes on one side of each pair.
const detectUtf16WithoutBom = (bytes: Uint8Array): DetectedEncoding | null => {
    let evenZeros = 0
    let oddZeros = 0
    for (let i = 0; i < bytes.length; i++) {
        if (bytes[i] === 0) {
            if (i % 2 === 0) evenZeros++
            else oddZeros++
        }
    }
    const pairs = bytes.length / 2
    if (pairs === 0) return null
    if (oddZeros / pairs > 0.3) return 'utf-16le'
    if (evenZeros / pairs > 0.3) return 'utf-16be'
    return null
}

// Streams the whole file through a strict decoder so invalid bytes anywhere are caught, without holding it in memory.
const isValidUtf8 = async (file: File): Promise<boolean> => {
    try {
        await file
            .stream()
            .pipeThrough(new TextDecoderStream('utf-8', { fatal: true }))
            .pipeTo(new WritableStream())
        return true
    } catch {
        return false
    }
}

export async function detectFileEncoding(file: File): Promise<DetectedEncoding> {
    const sample = new Uint8Array(await file.slice(0, Math.min(SAMPLE_BYTES, file.size)).arrayBuffer())

    const fromBom = detectFromBom(sample) ?? detectUtf16WithoutBom(sample)
    if (fromBom) return fromBom

    if (typeof TextDecoderStream === 'undefined') return 'unknown'

    // Not valid UTF-8 and no BOM: almost always an Excel/Windows export in Windows-1252.
    return (await isValidUtf8(file)) ? 'utf-8' : 'windows-1252'
}

// TextDecoder strips a UTF-8 BOM by default, so it decodes the same as plain UTF-8.
export function toDecoderLabel(encoding: DetectedEncoding): string {
    if (encoding === 'utf-8-bom' || encoding === 'unknown') return 'utf-8'
    return encoding
}
